import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, colors, fmtMoney, fmtMoneyK, fmtPct, fmtDate, fmtDateTime, yearOptions, currentYear, useIsMobile, mobile } from '../components/UI';
import TasksWidget from '../components/TasksWidget';
import CalendarWidget from '../components/CalendarWidget';
import PerformanceTargetsWidget from '../components/PerformanceTargetsWidget';
import TeamPaceBoard from '../components/TeamPaceBoard';
import AmDrillModal from '../components/AmDrillModal';
import BranchBudgetWidget from '../components/BranchBudgetWidget';
import PendingInvitationsWidget from '../components/PendingInvitationsWidget';
import BilledDrillModal from '../components/BilledDrillModal';
import FollowUpModal from '../components/FollowUpModal';
import { WhatsNewCard } from '../components/WhatsNew';

// Compact month+day for card chips ("Jun 29") — the full fmtDate with year
// made the OVERDUE chip half the card wide and squeezed the title beside it.
const fmtDateShort = (d) => {
  if (!d) return '';
  const m = String(d).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fmtDate(d);
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  dt.setFullYear(Number(m[1]));
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Notes on the dashboard cards carry the entry's ENTIRE running note history
// (every appended follow-up block), so unclamped they made one card a full
// screen tall on a phone. Clamp to 3 lines with an explicit Show more/less.
function ClampedNote({ text, label = 'Notes' }) {
  const [expanded, setExpanded] = useState(false);
  const long = String(text).length > 140 || String(text).split('\n').length > 3;
  return (
    <div style={{ marginTop: 8, borderTop: '1px solid #F3F4F6', paddingTop: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{
        fontSize: 13, lineHeight: 1.5, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' })
      }}>
        {text}
      </div>
      {long && (
        <button type="button" onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: 'none', padding: '6px 0 0', color: colors.orange, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
          {expanded ? '▲ Show less' : '▼ Show more'}
        </button>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [byRep, setByRep] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [flagged, setFlagged] = useState([]);
  // (Old quota state removed -- PerformanceTargetsWidget loads its own data.)
  const [branches, setBranches] = useState([]);
  const [reps, setReps] = useState([]);
  // kind filters the Activity KPIs only -- Base = day-to-day branch business,
  // Project = larger turnaround/opportunity. Empty string = both ('All').
  const [filter, setFilter] = useState({ branch_id: '', owner_id: '', year: String(currentYear()), kind: '' });
  // Scope toggle for the Tasks + Calendar widgets. Reps only ever see their
  // own (no toggle). Supervisor / leadership / admin default to 'team' so
  // they land on their employees' assigned items, with a one-click flip
  // back to their personal view.
  const canSwitchScope = user.role !== 'rep' && user.role !== 'ops';
  const [viewScope, setViewScope] = useState(canSwitchScope ? 'team' : 'mine');
  const [error, setError] = useState('');
  // Drill-down state for the Billed (Actual) tiles. drillKind tracks which
  // category the user clicked on; null = modal closed. MUST be declared up
  // here with the other hooks -- placing it below the early-return guards
  // breaks the React rules-of-hooks (different hook count per render).
  const [drillKind, setDrillKind] = useState(null);
  // Follow-ups Due entry being logged right here on the dashboard (null =
  // modal closed). Same modal + endpoint as the Activity Log's + Follow-up.
  const [followingUp, setFollowingUp] = useState(null);
  // AM drill modal (click a row on the By Account Manager scoreboard) +
  // scoreboard sort state (click a header; pipeline DESC is the old default).
  const [drillAm, setDrillAm] = useState(null);
  const [byRepSort, setByRepSort] = useState({ key: 'pipeline_value', dir: 'desc' });
  const isMobile = useIsMobile();

  // Re-pull the Follow-ups Due list after logging one from the dashboard --
  // rolling the date forward (or closing the entry) drops it off the list.
  const reloadFollowUps = () => {
    const fuParams = {};
    if (filter.branch_id) fuParams.branch_id = filter.branch_id;
    if (filter.owner_id) fuParams.owner_id = filter.owner_id;
    if (filter.kind) fuParams.kind = filter.kind;
    api.followUpsToday(fuParams).then(setFollowUps).catch(() => {});
  };

  // Fetch dropdown options once -- only roles that can filter need them.
  useEffect(() => {
    if (user.role === 'rep') return;
    api.listBranches().then(setBranches).catch(() => {});
    api.listReps().then(setReps).catch(() => {});
  }, [user.role]);

  // Reload KPIs whenever filter changes -- pass the filter to all three
  // panels so KPIs, Follow-ups, and By-Rep all respect branch/rep/year.
  useEffect(() => {
    const params = {};
    if (filter.branch_id) params.branch_id = filter.branch_id;
    if (filter.owner_id) params.owner_id = filter.owner_id;
    if (filter.year) params.year = filter.year;
    if (filter.kind) params.kind = filter.kind;
    api.kpis(params).then(setKpis).catch(e => setError(e.message));

    // Follow-ups doesn't filter by year (call log entries aren't year-bucketed
    // the same way) -- but does respect branch + rep + kind.
    const fuParams = {};
    if (filter.branch_id) fuParams.branch_id = filter.branch_id;
    if (filter.owner_id) fuParams.owner_id = filter.owner_id;
    if (filter.kind) fuParams.kind = filter.kind;
    api.followUpsToday(fuParams).then(setFollowUps).catch(() => {});

    if (user.role !== 'rep') {
      api.byRep(params).then(setByRep).catch(() => {});
      // Flagged honors the same branch/AM/kind filters as the rest of the
      // dashboard (it previously ignored branch/AM — Ali review 2026-07-07).
      const flagParams = {};
      if (filter.kind) flagParams.kind = filter.kind;
      if (filter.branch_id) flagParams.branch_id = filter.branch_id;
      if (filter.owner_id) flagParams.owner_id = filter.owner_id;
      api.flaggedCallLog(flagParams).then(setFlagged).catch(() => {});
    }
    // (Performance Targets are loaded inside PerformanceTargetsWidget.)
  }, [user.role, filter.branch_id, filter.owner_id, filter.year, filter.kind]);

  // Scoreboard sorting — click a header to re-rank the AMs. Strings sort
  // alphabetically, everything else numerically; null-safe. MUST stay above
  // the early-return guards (rules-of-hooks — same constraint as drillKind).
  const sortedByRep = useMemo(() => {
    const { key, dir } = byRepSort;
    const mul = dir === 'asc' ? 1 : -1;
    const stringKeys = new Set(['rep_name', 'branch_name']);
    return [...byRep].sort((a, b) => {
      if (stringKeys.has(key)) return mul * String(a[key] || '').localeCompare(String(b[key] || ''));
      if (key === 'last_contact_at' || key === 'last_active_at') {
        return mul * ((a[key] ? new Date(a[key]).getTime() : 0) - (b[key] ? new Date(b[key]).getTime() : 0));
      }
      return mul * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
    });
  }, [byRep, byRepSort]);

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!kpis) return <div>Loading...</div>;

  // Reps shown in the dropdown reflect the chosen branch (admin) or the
  // supervisor's own branch (server already scopes /lookups/reps for them).
  const repsForBranch = filter.branch_id
    ? reps.filter(r => String(r.branch_id) === String(filter.branch_id))
    : reps;

  const sortTh = (label, key, title) => {
    const active = byRepSort.key === key;
    return (
      <th
        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        title={title || `Sort by ${label}`}
        onClick={() => setByRepSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
      >
        {label} <span style={{ fontSize: 9, color: active ? colors.orange : '#9CA3AF' }}>{active ? (byRepSort.dir === 'desc' ? '▼' : '▲') : '⇅'}</span>
      </th>
    );
  };

  // "Single rep scope" -- when the BILLED tile sub-line should show the
  // home/elsewhere split (which is meaningful for one rep) instead of
  // the rentals/labor/consumables breakdown (which is meaningful at
  // any aggregate level). True when: viewer IS a rep (scope = self) OR
  // an admin/supervisor has filtered to one specific AM.
  const isSingleRepScope = user.role === 'rep' || !!filter.owner_id;

  // Scope label
  const branchName = branches.find(b => String(b.id) === String(filter.branch_id))?.name;
  const repName = reps.find(r => String(r.id) === String(filter.owner_id))?.full_name;
  const baseScope = user.role === 'rep' ? 'You'
              : repName ? `Rep — ${repName}`
              : branchName ? `Branch — ${branchName}`
              : user.role === 'supervisor' ? 'My Branch'
              : 'Company';
  const yearLabel = filter.year === 'undated' ? 'No date' : (filter.year || 'All years');
  const scope = `${baseScope} · ${yearLabel}`;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>
          Sales Dashboard <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 14 }}>· {scope}</span>
        </h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            style={{ ...styles.input, width: 140 }}
            value={filter.year}
            onChange={e => setFilter({ ...filter, year: e.target.value })}
          >
            <option value="">All years</option>
            {yearOptions().map(y => <option key={y} value={String(y)}>{y}</option>)}
            <option value="undated">No date</option>
          </select>
          {/* Activity kind segmented control -- splits the Activity KPIs by
              Base business vs Project. Empty = both. Project KPIs (pipeline,
              won, etc.) come from the projects table and aren't affected. */}
          <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            {[
              { v: '',        label: 'All' },
              { v: 'Base',    label: 'Base' },
              { v: 'Project', label: 'Project' }
            ].map(opt => {
              const active = filter.kind === opt.v;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setFilter({ ...filter, kind: opt.v })}
                  style={{
                    padding: '0 14px',
                    height: 38,
                    border: 'none',
                    background: active ? '#E36F2C' : '#fff',
                    color: active ? '#fff' : '#374151',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                  title="Filter the activity KPIs by Base business vs Project"
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {user.role !== 'rep' && isAdminLike(user) && (
            <select
              style={{ ...styles.input, width: 220 }}
              value={filter.branch_id}
              onChange={e => setFilter({ ...filter, branch_id: e.target.value, owner_id: '' })}
            >
              <option value="">All branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </select>
          )}
          {user.role !== 'rep' && (
            <select
              style={{ ...styles.input, width: 240 }}
              value={filter.owner_id}
              onChange={e => setFilter({ ...filter, owner_id: e.target.value })}
            >
              <option value="">All account managers</option>
              {repsForBranch.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
            </select>
          )}
          {(filter.branch_id || filter.owner_id || filter.kind || filter.year !== String(currentYear())) && (
            <button style={styles.btnSecondary} onClick={() => setFilter({ branch_id: '', owner_id: '', year: String(currentYear()), kind: '' })}>Reset</button>
          )}
        </div>
      </div>

      {/* Personalized hero greeting -- everyone sees this on Dashboard load.
          Name + time-of-day greeting + day-of-week tone + a single
          context-aware actionable hint pulled from their data. */}
      <PersonalGreeting user={user} kpis={kpis} followUps={followUps} flagged={flagged} />

      {/* What's New -- product changelog, visible to every role. Hides itself
          when there are no entries. The topbar 🔔 owns the unread count; this
          card just surfaces the latest updates with a NEW pill. */}
      <WhatsNewCard />

      {/* Pending event invitations -- hides itself when empty. Sits at the
          top because it's an inbox-style action (someone needs your reply). */}
      {user.role !== 'ops' && <PendingInvitationsWidget user={user} />}

      {/* Performance Targets -- 6-tier annual ladder per rep with
          Annual / Quarter / Month period toggle. Replaces the older
          single-quota widget. Hidden for ops (no sales targets). */}
      {/* Reps see their own ladder; everyone managing AMs (supervisor /
          leadership / admin) gets the Team Pace Board instead — all AMs'
          realized-vs-target side by side (Ali review 2026-07-07). */}
      {user.role === 'rep' && <PerformanceTargetsWidget />}
      {user.role !== 'ops' && user.role !== 'rep' && <TeamPaceBoard />}

      {/* Branch Budget -- monthly Base + TA Budget / Forecast / Realized
          summary for the user's branch. Visible to anyone with a branch
          (reps see their branch alongside personal targets). */}
      {user.role !== 'ops' && user.branch_id && <BranchBudgetWidget />}

      {/* Mine / Team scope toggle for the Tasks + Calendar widgets.
          Supervisor lands on Team by default to see their reports' work;
          one click flips to their own personal queue. Reps don't see the
          toggle (their team and their mine are the same). */}
      {canSwitchScope && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>VIEW:</span>
          <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
            {[
              { v: 'team', label: '👥 Team' },
              { v: 'mine', label: '🙋 Mine' }
            ].map(opt => {
              const active = viewScope === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setViewScope(opt.v)}
                  style={{
                    padding: '0 14px', height: 34, border: 'none',
                    background: active ? colors.charcoal : '#fff',
                    color: active ? '#fff' : colors.charcoal,
                    fontWeight: 600, fontSize: 13, cursor: 'pointer'
                  }}
                  title={opt.v === 'team'
                    ? "Tasks + calendar for your direct reports"
                    : "Your personal tasks + calendar"}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 12, color: colors.textMuted }}>
            {viewScope === 'team'
              ? "Showing your team's tasks and calendar"
              : 'Showing your personal tasks and calendar'}
          </span>
        </div>
      )}

      {/* Tasks -- personal to-do, visible to everyone. Honors the
          Base/Project toggle so a sales rep working in Base mode sees
          their Base tasks and any new ones default to Base. */}
      <TasksWidget kind={filter.kind} scope={viewScope} />

      {/* Compact 7-day calendar -- same scope + kind as Tasks. */}
      <CalendarWidget kind={filter.kind} scope={viewScope} />

      {/* Kind toggle splits the BILLED (ACTUAL) tile by Rentalman category:
          Base mode = non-08xx Loc, Project mode = 08xx Loc (TA-DIV-800).
          Together they're a clean partition of every Rentalman invoice
          credited to the accessible reps. Other Project-mode tiles (In
          the Hunt, Pipeline, Weighted, Forecast, Win Rate, Avg Deal)
          stay tied to the Project Tracker -- those are pre-bid /
          opportunity metrics with no Rentalman equivalent. */}
      <div style={styles.kpiGrid}>
        {filter.kind === 'Base' ? (
          <>
            <Kpi label="In the Hunt"        value="—" sub="Project Tracker metric" help={KPI_HELP.hunt} />
            <Kpi label="Pipeline"           value="—" sub="Project Tracker metric" help={KPI_HELP.pipeline} />
            <Kpi label="Weighted Pipeline"  value="—" sub="Project Tracker metric" help={KPI_HELP.weighted} />
            <Kpi label="Forecast (Awarded)" value="—" sub="Project Tracker metric" help={KPI_HELP.forecast} />
            <Kpi
              label="Billed (Actual)"
              value={fmtMoneyK(kpis.base_billed || 0)}
              sub={baseBilledSub(kpis, isSingleRepScope)}
              help={KPI_HELP.baseBilled}
              onClick={() => setDrillKind('Base')}
            />
            <Kpi label="Win Rate (12mo)"    value="—" sub="Project Tracker metric" help={KPI_HELP.winRate} />
            <Kpi label="Avg Deal Size"      value="—" sub="Project Tracker metric" help={KPI_HELP.avgDeal} />
          </>
        ) : (
          <>
            <Kpi
              label="In the Hunt"
              value={fmtMoneyK(kpis.hunt_value)}
              sub={`${kpis.hunt_count} project${kpis.hunt_count === 1 ? '' : 's'} · pre-bid`}
              help={KPI_HELP.hunt}
            />
            <Kpi label="Pipeline" value={fmtMoneyK(kpis.pipeline_value)} sub={`${kpis.open_count} open${kpis.stuck_count > 0 ? ` · ${kpis.stuck_count} stuck` : ''}`} help={KPI_HELP.pipeline} />
            <Kpi label="Weighted Pipeline" value={fmtMoneyK(kpis.weighted_pipeline)} sub="estimated × % win" help={KPI_HELP.weighted} />
            <Kpi
              label="Forecast (Awarded)"
              value={fmtMoneyK(kpis.won_forecast_value)}
              sub={`${kpis.won_count} deal${kpis.won_count === 1 ? '' : 's'} · estimated`}
              help={KPI_HELP.forecast}
            />
            {filter.kind === 'Project' ? (
              <Kpi
                label="Billed (Actual)"
                value={fmtMoneyK(kpis.ta_billed || 0)}
                sub={taBilledSub(kpis)}
                help={KPI_HELP.taBilled}
                onClick={() => setDrillKind('TA')}
              />
            ) : (
              // "All" mode: Base + TA combined = total Rentalman billed.
              // This matches Performance Targets Realized for the scope.
              <Kpi
                label="Billed (Actual)"
                value={fmtMoneyK((Number(kpis.base_billed) || 0) + (Number(kpis.ta_billed) || 0))}
                sub={allBilledSub(kpis)}
                help={KPI_HELP.allBilled}
                onClick={() => setDrillKind('All')}
              />
            )}
            <Kpi label="Win Rate (12mo)" value={fmtPct(kpis.win_rate)} sub={`${kpis.won_count} won / ${kpis.lost_count} lost`} help={KPI_HELP.winRate} />
            <Kpi label="Avg Deal Size" value={fmtMoneyK(kpis.avg_deal_size)} sub="won deals" help={KPI_HELP.avgDeal} />
          </>
        )}
        <Kpi label="Activity (this week)" value={kpis.entries_this_week} sub={`${kpis.entries_30d} in last 30 days`} help={KPI_HELP.activity} />
        <Kpi label="Last Activity" value={fmtDateTime(kpis.last_activity_at) || '—'} sub="any rep" help={KPI_HELP.lastActivity} />
      </div>

      {flagged.length > 0 && (
        <div id="flagged-section" style={{ ...styles.card, borderColor: '#92400E', background: '#FFFBEB' }}>
          <h2 style={{ ...styles.h2, color: '#92400E' }}>⚑ Flagged for Follow-up with Rep <span style={{ color: '#6B7280', fontWeight: 400 }}>· {flagged.length}</span></h2>
          {isMobile ? (
            <div>
              {flagged.map(f => (
                /* Compact card: title + date chip on top, one muted meta line,
                   clamped note. The old label/value rows spent half the card
                   on right-aligned wrapping names — read as thrown-together. */
                <div key={f.id} style={{ ...mobile.listItem, background: '#fff', borderColor: '#FCD34D' }}>
                  {/* Meta line sits BELOW the header row at full card width —
                      inside the title column it inherited the chip squeeze
                      and wrapped word-by-word. */}
                  {/* flexWrap + the 140px title basis: under Safari page zoom
                      the CSS viewport can be ~300px — the chip then wraps to
                      its own line instead of crushing the title. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px', minWidth: 0, fontSize: 15, fontWeight: 700, color: '#1F2937', lineHeight: 1.3 }}>
                      {f.customer}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#92400E', background: '#FEF3C7',
                      padding: '3px 8px', borderRadius: 8, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      ⚑ {fmtDateShort(f.supervisor_flag_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                    {f.rep_name}{f.contact_name ? ` · ${f.contact_name}` : ''}
                  </div>
                  <div style={{ fontSize: 11, color: '#92400E', fontWeight: 600, marginTop: 4 }}>
                    Flagged by {f.flagged_by_name || '—'}
                  </div>
                  {f.notes && <ClampedNote text={f.notes} label="Note" />}
                </div>
              ))}
            </div>
          ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Flagged</th>
                <th style={styles.th}>By</th>
                <th style={styles.th}>Account Mgr</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map(f => (
                <tr key={f.id}>
                  <td style={styles.td}>{fmtDateTime(f.supervisor_flag_at)}</td>
                  <td style={styles.td}>{f.flagged_by_name || '—'}</td>
                  <td style={styles.td}>{f.rep_name}</td>
                  <td style={styles.td}><strong>{f.customer}</strong></td>
                  <td style={styles.td}>{f.contact_name}</td>
                  <td style={{ ...styles.td, maxWidth: 380, whiteSpace: 'pre-wrap', fontSize: 12 }}>{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
          <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8, marginBottom: 0 }}>
            Clear a flag from <em>Call Log</em> by clicking the Flagged button on the entry.
          </p>
        </div>
      )}

      {followUps.length > 0 && (
        <div id="followups-section" style={styles.card}>
          <h2 style={styles.h2}>Follow-ups Due <span style={{ color: '#6B7280', fontWeight: 400 }}>· {followUps.length}</span></h2>
          {isMobile ? (
            <div>
              {followUps.map(f => {
                const fuOverdue = f.follow_up_date && String(f.follow_up_date).slice(0, 10) < new Date().toISOString().slice(0, 10);
                return (
                  /* Same compact treatment as the flagged cards: title + due
                     chip, one meta line, clamped notes, action row. */
                  <div key={f.id} style={mobile.listItem}>
                    {/* Meta below the header row at full width — see the
                        flagged card note above. Chip date is month+day. */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 140px', minWidth: 0, fontSize: 15, fontWeight: 700, color: '#1F2937', lineHeight: 1.3 }}>
                        {f.customer}
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
                        padding: '3px 8px', borderRadius: 8,
                        color: fuOverdue ? '#B91C1C' : '#92400E',
                        background: fuOverdue ? '#FEE2E2' : '#FEF3C7'
                      }}>
                        {fuOverdue ? 'OVERDUE · ' : 'Due '}{fmtDateShort(f.follow_up_date)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                      {f.rep_name}{f.contact_name ? ` · ${f.contact_name}` : ''}{f.follow_up_type ? ` · ${f.follow_up_type}` : ''}
                    </div>
                    {f.notes && <ClampedNote text={f.notes} />}
                    <div style={mobile.listActions}>
                      <button style={{ ...styles.btn, ...mobile.btnTap }}
                        title="Log this follow-up right here — appends a dated note and rolls the date forward"
                        onClick={() => setFollowingUp(f)}>
                        + Follow-up
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Due</th>
                <th style={styles.th}>Account Mgr</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Notes</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {followUps.map(f => (
                <tr key={f.id}>
                  <td style={styles.td}>{fmtDate(f.follow_up_date)}</td>
                  <td style={styles.td}>{f.rep_name}</td>
                  <td style={styles.td}><strong>{f.customer}</strong></td>
                  <td style={styles.td}>{f.contact_name}</td>
                  <td style={styles.td}>{f.follow_up_type}</td>
                  <td style={{ ...styles.td, maxWidth: 380, whiteSpace: 'pre-wrap', fontSize: 12 }}>{f.notes}</td>
                  <td style={styles.td}>
                    <button style={{ ...styles.btn, fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
                      title="Log this follow-up right here — appends a dated note and rolls the date forward"
                      onClick={() => setFollowingUp(f)}>
                      + Follow-up
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      )}

      {user.role !== 'rep' && byRep.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>
            By Account Manager
            {filter.kind === 'Base' && (
              <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
                · Project Tracker $ columns hidden in Base mode
              </span>
            )}
          </h2>
          {isMobile ? (
            <div>
              {sortedByRep.map(r => {
                const isBase = filter.kind === 'Base';
                return (
                  <div key={r.rep_id} style={mobile.listItem}>
                    <div style={mobile.listItemHeader}>
                      <span style={mobile.listItemTitle}>{r.rep_name}</span>
                      <span style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>{r.branch_name || '—'}</span>
                    </div>
                    {!isBase && (
                      <>
                        <div style={mobile.listRow}>
                          <span style={mobile.listLabel}>Pipeline · Weighted</span>
                          <span style={mobile.listValue}>{fmtMoney(r.pipeline_value)} · {fmtMoney(r.weighted_pipeline)}</span>
                        </div>
                        <div style={mobile.listRow}>
                          <span style={mobile.listLabel}>Open · Stuck · Won · Lost</span>
                          <span style={mobile.listValue}>
                            {r.open_count ?? 0} · <span style={{ color: r.stuck_count > 0 ? colors.red : undefined, fontWeight: r.stuck_count > 0 ? 700 : undefined }}>{r.stuck_count ?? 0}</span> · {r.won_count} · {r.lost_count}
                          </span>
                        </div>
                        <div style={mobile.listRow}>
                          <span style={mobile.listLabel}>Win % (12 mo)</span>
                          <span style={mobile.listValue}>{fmtPct(r.win_rate)}</span>
                        </div>
                        <div style={mobile.listRow}>
                          <span style={mobile.listLabel}>Forecast · Billed</span>
                          <span style={mobile.listValue}>{fmtMoney(r.won_forecast_value)} · {fmtMoney(r.won_billed_value)}</span>
                        </div>
                      </>
                    )}
                    <div style={mobile.listRow} title="Rentalman invoice revenue credited to this AM (Base + TA)">
                      <span style={mobile.listLabel}>Realized (Rentalman)</span>
                      <span style={{ ...mobile.listValue, fontWeight: 700 }}>{fmtMoney(r.realized_value)}</span>
                    </div>
                    <div style={mobile.listRow}>
                      <span style={mobile.listLabel}>Overdue F/U · Unscheduled</span>
                      <span style={{ ...mobile.listValue, color: r.overdue_followups > 0 ? colors.red : undefined, fontWeight: r.overdue_followups > 0 ? 700 : undefined }}>
                        {r.overdue_followups} · {r.no_next_followup}
                      </span>
                    </div>
                    <div style={mobile.listRow}>
                      <span style={mobile.listLabel}>Activity (this week)</span>
                      <span style={mobile.listValue}>{r.entries_this_week}</span>
                    </div>
                    <div style={mobile.listRow}>
                      <span style={mobile.listLabel}>Last Contact</span>
                      <span style={mobile.listValue}>{fmtDateTime(r.last_contact_at) || '—'}</span>
                    </div>
                    <div style={mobile.listActions}>
                      <button style={{ ...styles.btnSecondary, ...mobile.btnTap, flex: 1 }} onClick={() => setDrillAm(r.rep_id)}>
                        📊 AM Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {sortTh('Account Mgr', 'rep_name')}
                {sortTh('Branch', 'branch_name')}
                {sortTh('Pipeline', 'pipeline_value')}
                {sortTh('Weighted', 'weighted_pipeline')}
                {sortTh('Open', 'open_count')}
                {sortTh('Stuck', 'stuck_count', 'Bidding deals untouched for 30+ days')}
                {sortTh('Won', 'won_count')}
                {sortTh('Lost', 'lost_count')}
                {sortTh('Win %', 'win_rate', 'Rolling 12 months — same definition as the Win Rate tile')}
                {sortTh('Forecast', 'won_forecast_value')}
                {sortTh('Billed', 'won_billed_value', 'Project Tracker actuals on awarded deals')}
                {sortTh('Realized', 'realized_value', 'Rentalman invoice revenue credited to the AM (Base + TA) — the single source of truth')}
                {sortTh('This Wk', 'entries_this_week')}
                {sortTh('Ovd F/U', 'overdue_followups', 'Overdue follow-ups (hover a row count for unscheduled entries too)')}
                {sortTh('Last Contact', 'last_contact_at')}
                {sortTh('Last Active', 'last_active_at')}
              </tr>
            </thead>
            <tbody>
              {sortedByRep.map(r => {
                const isBase = filter.kind === 'Base';
                return (
                <tr key={r.rep_id} style={{ cursor: 'pointer' }} title="Click for the AM drill-down"
                  onClick={() => setDrillAm(r.rep_id)}>
                  <td style={styles.td}><strong style={{ color: colors.orange }}>{r.rep_name}</strong></td>
                  <td style={styles.td}>{r.branch_name || '—'}</td>
                  <td style={styles.td}>{isBase ? '—' : fmtMoney(r.pipeline_value)}</td>
                  <td style={styles.td}>{isBase ? '—' : fmtMoney(r.weighted_pipeline)}</td>
                  <td style={styles.td}>{isBase ? '—' : (r.open_count ?? 0)}</td>
                  <td style={{ ...styles.td, color: r.stuck_count > 0 ? colors.red : undefined, fontWeight: r.stuck_count > 0 ? 700 : undefined }}>
                    {isBase ? '—' : (r.stuck_count ?? 0)}
                  </td>
                  <td style={styles.td}>{isBase ? '—' : r.won_count}</td>
                  <td style={styles.td}>{isBase ? '—' : r.lost_count}</td>
                  <td style={styles.td}>{isBase ? '—' : fmtPct(r.win_rate)}</td>
                  <td style={styles.td}>{isBase ? '—' : fmtMoney(r.won_forecast_value)}</td>
                  <td
                    style={styles.td}
                    title={isBase ? '' : (r.won_count > 0 ? `${r.won_billed_count} of ${r.won_count} billed` : '')}
                  >
                    {isBase ? '—' : fmtMoney(r.won_billed_value)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 700 }}>{fmtMoney(r.realized_value)}</td>
                  <td style={styles.td}>{r.entries_this_week}</td>
                  <td style={{ ...styles.td, color: r.overdue_followups > 0 ? colors.red : undefined, fontWeight: r.overdue_followups > 0 ? 700 : undefined }}
                    title={r.no_next_followup > 0 ? `+${r.no_next_followup} open entr${r.no_next_followup === 1 ? 'y' : 'ies'} with no next follow-up scheduled` : ''}>
                    {r.overdue_followups}
                  </td>
                  <td style={styles.td}>{fmtDateTime(r.last_contact_at) || '—'}</td>
                  <td style={styles.td}>{r.last_active_at ? fmtDateTime(r.last_active_at) : <span style={{ color: '#9CA3AF' }}>Never</span>}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      )}

      {/* AM drill-down — coaching view for one Account Manager. */}
      {drillAm && (
        <AmDrillModal amId={drillAm} year={filter.year} onClose={() => setDrillAm(null)} />
      )}

      {/* Sales Revenue drill-down modal -- opens when admin/rep clicks
          a Billed (Actual) tile in Base / TA / All mode. Same scope as
          the tile so the totals reconcile. */}
      {drillKind && (
        <BilledDrillModal
          kind={drillKind}
          year={filter.year || undefined}
          branchId={filter.branch_id || undefined}
          ownerId={filter.owner_id || undefined}
          scopeLabel={scope}
          onClose={() => setDrillKind(null)}
        />
      )}

      {/* Log a due follow-up without leaving the dashboard — same modal +
          endpoint as the Activity Log's + Follow-up. A failed save alerts
          and keeps the modal open (page-level setError would blank the
          whole dashboard). */}
      {followingUp && (
        <FollowUpModal
          entry={followingUp}
          onCancel={() => setFollowingUp(null)}
          onSave={async (body) => {
            try {
              await api.addCallLogFollowUp(followingUp.id, body);
              setFollowingUp(null);
              reloadFollowUps();
            } catch (err) {
              alert(`Save failed: ${err.message}`);
            }
          }}
        />
      )}
    </>
  );
}

// Kpi tile -- optional `help` renders a small (?) glyph in the corner with
// a native browser tooltip explaining what the KPI calculates. Tooltip
// also applies to the whole card so any hover surfaces the explanation.
//
// onClick: when provided, the tile becomes clickable (hover lift + cursor)
// and fires the handler. Used to open drill-down modals from the Billed
// (Actual) tiles. The (?) help glyph stops propagation so hovering it
// doesn't trigger the click.
const Kpi = ({ label, value, sub, help, onClick }) => {
  const clickable = !!onClick;
  return (
    <div
      style={{
        ...styles.kpiCard,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform 0.1s, box-shadow 0.1s',
        ...(clickable ? { ':hover': {} } : {})
      }}
      title={help || undefined}
      onClick={clickable ? onClick : undefined}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      } : undefined}
    >
      <div style={{ ...styles.kpiLabel, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span>{label}{clickable && <span style={{ marginLeft: 4, fontSize: 10, color: '#9CA3AF' }}>↗</span>}</span>
        {help && (
          <span
            title={help}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: '50%',
              border: '1px solid #9CA3AF', color: '#6B7280',
              fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0
            }}
            aria-label={`Help: ${label}`}
          >?</span>
        )}
      </div>
      <div style={styles.kpiValue}>{value}</div>
      {sub && <div style={styles.kpiSubtext}>{sub}</div>}
    </div>
  );
};

// Hover-tooltip text for each KPI tile. Plain English, no jargon. Status
// names match the Project Tracker labels (In the Hunt / Bidding / Won /
// Active / Complete / Lost / Closed). "Closed" is naturally absent from
// every $-counting KPI -- it's a terminal "removed from pipeline" state,
// neither won nor lost.
const KPI_HELP = {
  hunt:
    'Estimated $ of every Project Tracker entry in "In the Hunt" status — ' +
    'opportunities you are aware of but have not bid yet.',
  pipeline:
    'Estimated $ of every Project Tracker entry in "Bidding" or "Mobilization Standby" ' +
    'status — deals you have quoted and are waiting on. Excludes In the Hunt and awarded deals.',
  weighted:
    'Pipeline adjusted by each deal\'s % win odds. A more realistic forecast — ' +
    'a $100K bid at 50% win = $50K weighted.',
  forecast:
    'Quoted $ on every awarded Project Tracker deal (Won, Active, or Complete) — ' +
    'what you projected the deal would be worth when it landed. ' +
    'For total billed revenue see Performance Targets above.',
  billed:
    'Invoiced $ on awarded Project Tracker deals (Won / Active / Complete) where ' +
    'the actual amount has been entered. ' +
    'NOTE: this is a view of the larger tracked deals — those same invoices ' +
    'are already inside the Rentalman total shown on Performance Targets and ' +
    'on the Base tile. It is NOT additive to Base.',
  baseBilled:
    'Every Rentalman invoice in scope with a Loc that is NOT 08xx ' +
    '(rentals + labor + consumables + unallocated). Source of truth for Base ' +
    'billed revenue. TA work (08xx Loc) is in the Project mode tile.\n\n' +
    'When you (or admin filtered to one AM) view a single rep, the sub-line ' +
    'shows that rep\'s home / elsewhere split (which branch\'s yard the ' +
    'invoice booked at). At Corporate / multi-rep views the sub-line shows ' +
    'the rentals / labor / consumables breakdown instead — home/elsewhere ' +
    'isn\'t meaningful when summed across many reps.',
  taBilled:
    'Every Rentalman invoice in scope with a Loc that IS 08xx — turnaround ' +
    'work that books to TA-DIV-800\'s P&L. Source of truth for TA billed ' +
    'revenue. Mirrors the Base mode tile (which sums every NON-08xx Loc) so ' +
    'together they\'re a clean partition of every Rentalman invoice for the ' +
    'scope. Reps still get full commission credit on their TA work via ' +
    'Performance Targets above (which sums Base + TA together).',
  allBilled:
    'Every Rentalman invoice in scope, Base + TA combined. Equals total ' +
    'billed revenue for the period. Same number as Performance Targets ' +
    'Realized when you\'re viewing a single rep. Flip to Base or Project ' +
    'mode to see only that half.',
  winRate:
    'Project Tracker deals decided in the last 12 months: wins ÷ (wins + losses). ' +
    'Wins = Won / Active / Complete. Losses = Lost. Excludes In the Hunt, Bidding, ' +
    'and Closed (no outcome).',
  avgDeal:
    'Average $ value of an awarded Project Tracker deal (Won / Active / Complete). ' +
    'Complete jobs use the billed amount when there is one; Won and Active jobs ' +
    'use the original quote, because their billing is still coming in.',
  activity:
    'Activity Log entries for the current week (week_of = this Monday). ' +
    'Subtext shows your last 30 days for a longer trend. Respects the Base / ' +
    'Project / All filter at the top.',
  lastActivity:
    'The most recent edit to any Activity Log entry in the current scope. A pulse ' +
    'check that someone is keeping the system current.'
};

// Subtext for the Billed (Actual) KPI: shows how many of the awarded deals
// have an actual_value entered, plus billed-as-percent-of-forecast so admin
// can see at a glance how much of the awarded book is invoiced.
const billedSub = (k) => {
  if (!k.won_count) return 'no awarded deals';
  if (!k.won_billed_count) return `0 of ${k.won_count} billed`;
  const pct = k.won_forecast_value > 0
    ? Math.round((k.won_billed_value / k.won_forecast_value) * 100)
    : 0;
  return `${k.won_billed_count} of ${k.won_count} billed · ${pct}% of forecast`;
};

// Subtext for the Base-mode Billed (Actual) tile.
//
// The home/elsewhere split is meaningful only when the scope is ONE
// rep -- it shows that rep's personal Base credit broken down by
// where the invoice booked (home branch yard vs another branch's).
// At multi-rep / corporate scope, the split is an aggregate that
// doesn't tell you anything useful, so fall back to the
// rentals/labor/consumables breakdown instead.
const baseBilledSub = (k, isSingleRepScope) => {
  const total = Number(k.base_billed || 0);
  if (total === 0) return 'no Sales Revenue entered yet';
  if (isSingleRepScope) {
    const home      = Number(k.base_billed_home || 0);
    const elsewhere = Number(k.base_billed_elsewhere || 0);
    // Cross-branch revenue meaningful? (≥1% of total filters rounding.)
    if (elsewhere > 0 && elsewhere >= total * 0.01) {
      return `${fmtMoneyK(home)} booked at home · ${fmtMoneyK(elsewhere)} at other branches`;
    }
  }
  // Aggregate scope OR single rep with no cross-branch revenue:
  // show the R/L/C split (useful for commission visibility).
  const r = Number(k.base_rentals || 0);
  const l = Number(k.base_labor || 0);
  const c = Number(k.base_consumables || 0);
  const parts = [];
  if (r > 0) parts.push(`${fmtMoneyK(r)} rentals`);
  if (l > 0) parts.push(`${fmtMoneyK(l)} labor`);
  if (c > 0) parts.push(`${fmtMoneyK(c)} consumables`);
  return parts.length ? parts.join(' · ') : '—';
};

// Subtext for the Project-mode Billed (Actual) tile (TA category from
// Rentalman -- 08xx Loc that books to TA-DIV-800). Shows the
// rentals/labor/consumables breakdown so the viewer can see what kind
// of TA work generated the revenue.
const taBilledSub = (k) => {
  const total = Number(k.ta_billed || 0);
  if (total === 0) return 'no TA revenue this period';
  const r = Number(k.ta_rentals || 0);
  const l = Number(k.ta_labor || 0);
  const c = Number(k.ta_consumables || 0);
  const parts = [];
  if (r > 0) parts.push(`${fmtMoneyK(r)} rentals`);
  if (l > 0) parts.push(`${fmtMoneyK(l)} labor`);
  if (c > 0) parts.push(`${fmtMoneyK(c)} consumables`);
  return parts.length ? parts.join(' · ') : 'books to TA-DIV-800';
};

// Subtext for the All-mode Billed (Actual) tile -- total Rentalman billed
// (Base + TA combined). Shows the partition so the viewer sees both halves.
const allBilledSub = (k) => {
  const base = Number(k.base_billed || 0);
  const ta   = Number(k.ta_billed   || 0);
  if (base + ta === 0) return 'no Rentalman revenue this period';
  return `${fmtMoneyK(base)} Base · ${fmtMoneyK(ta)} TA`;
};

// ---- Personalized greeting (top of every Dashboard load) ----------------

function PersonalGreeting({ user, kpis, followUps, flagged }) {
  const firstName = (user.full_name || '').split(/\s+/)[0] || 'there';
  const now = new Date();
  const hour = now.getHours();
  const dow = now.getDay(); // 0=Sun, 1=Mon

  // Time-of-day greeting + emoji
  const tod = hour < 5  ? { word: 'Working late',  icon: '🌙' }
            : hour < 11 ? { word: 'Good morning',  icon: '☀️' }
            : hour < 17 ? { word: 'Good afternoon', icon: '🌤️' }
            : hour < 21 ? { word: 'Good evening',  icon: '🌆' }
            :              { word: 'Burning the midnight oil', icon: '🌙' };

  // Day-of-week tone
  const dayMsg = dow === 1 ? "Let's get the week off to a strong start."
              : dow === 5 ? "Wrapping up the week — let's finish strong."
              : (dow === 0 || dow === 6) ? "Working the weekend? Here's where things stand."
              : "Here's what's on your plate today.";

  // Build a single context-aware highlight, picking the most actionable
  // signal in priority order. Each highlight optionally carries an action:
  //   - scrollTo: id of an element on this same Dashboard page
  //   - to: a route to navigate to (with optional query)
  // If neither is set the highlight renders as plain text.
  let highlight = { text: "You're caught up. Nice work." };
  if (followUps.length > 0) {
    highlight = {
      text: `${followUps.length} follow-up${followUps.length === 1 ? '' : 's'} due today →`,
      scrollTo: 'followups-section'
    };
  } else if (flagged && flagged.length > 0) {
    highlight = {
      text: `${flagged.length} rep note${flagged.length === 1 ? '' : 's'} flagged for follow-up →`,
      scrollTo: 'flagged-section'
    };
  } else if ((kpis.stuck_count || 0) > 0) {
    highlight = {
      text: `${kpis.stuck_count} stuck deal${kpis.stuck_count === 1 ? '' : 's'} need attention →`,
      to: '/projects?stuck=yes'
    };
  } else if (user.role === 'rep' && kpis.entries_this_week === 0 && (dow >= 2 && dow <= 5)) {
    highlight = {
      text: "You haven't logged any activity yet this week — let's get one on the board →",
      to: '/call-log'
    };
  }

  const dateLine = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
      color: '#fff', borderRadius: 8, padding: '20px 24px', marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {tod.icon} {tod.word}, {firstName}
        </h1>
        <span style={{ fontSize: 13, color: '#9CA3AF' }}>{dateLine}</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 14, color: '#D1D5DB' }}>
        {dayMsg}
      </div>
      <div style={{ marginTop: 10, fontSize: 14, fontWeight: 500 }}>
        <HighlightLine highlight={highlight} />
      </div>
    </div>
  );
}

const HighlightLine = ({ highlight }) => {
  const text = typeof highlight === 'string' ? highlight : highlight.text;
  const linkStyle = {
    color: '#FED7AA', textDecoration: 'none', cursor: 'pointer',
    borderBottom: '1px dashed rgba(254,215,170,0.5)', paddingBottom: 1
  };
  const plainStyle = { color: '#FED7AA' };

  if (highlight.to) {
    return <Link to={highlight.to} style={linkStyle}>{text}</Link>;
  }
  if (highlight.scrollTo) {
    return (
      <a
        href={`#${highlight.scrollTo}`}
        onClick={(e) => {
          e.preventDefault();
          document.getElementById(highlight.scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        style={linkStyle}
      >{text}</a>
    );
  }
  return <span style={plainStyle}>{text}</span>;
};
